import { Controller } from "@hotwired/stimulus";

/**
 * Scale Counter Controller
 *
 * Animates a number counting up from 1 to 10M+ with visual meter bars,
 * creating a "scaling up" effect for the "Scale from 0 to Hero" section.
 */
export default class extends Controller<HTMLElement> {
  static targets = ["number", "suffix", "bar1", "bar2", "bar3", "bar4", "bar5"];

  declare readonly numberTarget: HTMLElement;
  declare readonly suffixTarget: HTMLElement;
  declare readonly bar1Target: HTMLElement;
  declare readonly bar2Target: HTMLElement;
  declare readonly bar3Target: HTMLElement;
  declare readonly bar4Target: HTMLElement;
  declare readonly bar5Target: HTMLElement;

  private observer: IntersectionObserver | null = null;
  private hasAnimated: boolean = false;

  connect(): void {
    this.setupIntersectionObserver();
  }

  disconnect(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  private setupIntersectionObserver(): void {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.hasAnimated) {
            this.hasAnimated = true;
            this.animateScale();
          }
        });
      },
      { threshold: 0.3 }
    );

    this.observer.observe(this.element);
  }

  private animateScale(): void {
    const phases = [
      { value: 1, suffix: "", duration: 0 },
      { value: 100, suffix: "", duration: 400 },
      { value: 1, suffix: "K", duration: 600 },
      { value: 10, suffix: "K", duration: 400 },
      { value: 100, suffix: "K", duration: 400 },
      { value: 1, suffix: "M", duration: 500 },
      { value: 10, suffix: "M", duration: 600 },
    ];

    let currentPhase = 0;
    let phaseStartTime: number | null = null;
    let previousValue = 1;

    const bars = [
      this.bar1Target,
      this.bar2Target,
      this.bar3Target,
      this.bar4Target,
      this.bar5Target,
    ];

    // Initialize bars
    bars.forEach((bar) => {
      bar.style.transform = "scaleX(0)";
    });

    const animate = (currentTime: number) => {
      if (currentPhase >= phases.length) {
        // Animation complete - show final state
        this.numberTarget.textContent = "10";
        this.suffixTarget.textContent = "M+";
        bars.forEach((bar) => {
          bar.style.transform = "scaleX(1)";
        });
        return;
      }

      const phase = phases[currentPhase];

      if (phaseStartTime === null) {
        phaseStartTime = currentTime;
        previousValue =
          currentPhase > 0 ? phases[currentPhase - 1].value : 1;
      }

      const elapsed = currentTime - phaseStartTime;
      const progress = phase.duration > 0 ? Math.min(elapsed / phase.duration, 1) : 1;

      // Easing function
      const easedProgress = this.easeOutCubic(progress);

      // Interpolate value
      const currentValue = Math.round(
        previousValue + (phase.value - previousValue) * easedProgress
      );

      // Update display
      this.numberTarget.textContent = currentValue.toLocaleString();
      this.suffixTarget.textContent = phase.suffix;

      // Update bars based on overall progress
      const overallProgress = (currentPhase + progress) / phases.length;
      bars.forEach((bar, index) => {
        const barThreshold = (index + 1) / bars.length;
        const barProgress = Math.min(
          Math.max((overallProgress - barThreshold + 0.2) / 0.2, 0),
          1
        );
        bar.style.transform = `scaleX(${barProgress})`;
      });

      if (progress >= 1) {
        currentPhase++;
        phaseStartTime = null;
      }

      requestAnimationFrame(animate);
    };

    requestAnimationFrame(animate);
  }

  private easeOutCubic(t: number): number {
    return 1 - Math.pow(1 - t, 3);
  }
}
